import { useState } from "react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2, Mail, MapPin, MessageSquare, Phone } from "lucide-react";
import { sendContact } from "@/lib/api";

const SUBJECTS = ["Founder Circle", "Partnership", "Press", "Careers", "Support"];

export default function Contact() {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: "", email: "", subject: "Founder Circle", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.name.trim() || !f.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) || !f.message.trim()) {
      setError("Name, valid email and message are required.");
      return;
    }
    setStatus("loading");
    try {
      await sendContact(f);
      setStatus("done");
    } catch {
      setStatus("error");
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <SEO
        slug="contact"
        title="Contact · Real people, real timezones — Instantxfer CashApp"
        description="A human replies within one business day. Press, security, or general — one address for each. Amsterdam · Lagos · Nairobi."
      />
      <PageHero
        eyebrow={t("pageHero.contact.eyebrow")}
        title={t("pageHero.contact.title")}
        italic={t("pageHero.contact.italic")}
        body={t("pageHero.contact.body")}
        testId="contact-hero"
      />

      <section className="pb-24 md:pb-32 bg-canvas">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left sidebar */}
          <div className="lg:col-span-4 space-y-4">
            <InfoCard icon={Mail} title="Email">
              <a href="mailto:support@instantxfer.com" className="link-reveal" data-testid="contact-email-link">
                support@instantxfer.com
              </a>
            </InfoCard>
            <InfoCard icon={Phone} title={t("contact.whatsApp")}>
              <a
                href="https://wa.me/1775387466"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-forest text-canvas hover:bg-forest/90 transition-colors text-sm font-medium"
                data-testid="contact-whatsapp-link"
                aria-label={t("contact.chatWhatsApp")}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.074-.297-.148-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.625.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347zM12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.304-1.654a11.87 11.87 0 005.741 1.462h.005c6.554 0 11.89-5.335 11.893-11.892C23.944 5.335 18.609 0 12.05 0z"/>
                </svg>
                {t("contact.chatWhatsApp")}
              </a>
              <div className="text-ink/50 text-xs mt-2">{t("contact.whatsAppNote")}</div>
            </InfoCard>
            <InfoCard icon={MessageSquare} title="In-app chat">
              Available to beta members 24/7 in every supported corridor.
            </InfoCard>
            <InfoCard icon={MapPin} title="Offices">
              <div>Brouwerijstraat 1B, Kampenhout (BE)</div>
              <div className="text-ink/60 text-xs mt-1">
                Addis Ababa · Amsterdam · Birmingham · Brussels · Cape Town · Douala · Lagos · London · Nairobi
              </div>
            </InfoCard>
          </div>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8 card-flat p-8 md:p-12"
            data-testid="contact-form"
          >
            {status === "done" ? (
              <div className="flex flex-col items-start gap-4 py-6" data-testid="contact-success">
                <div className="w-12 h-12 rounded-full bg-lime grid place-items-center">
                  <CheckCircle2 className="w-6 h-6 text-forest" />
                </div>
                <h3 className="font-display text-3xl text-forest tracking-tightest">Message received.</h3>
                <p className="text-ink/70 max-w-md">
                  We reply within one business day. If it&apos;s urgent, mention it in the subject line
                  and we&apos;ll bump it to the top of the queue.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Your name" value={f.name} onChange={(v) => set("name", v)} testId="contact-name" />
                  <Field
                    label="Email"
                    type="email"
                    value={f.email}
                    onChange={(v) => set("email", v)}
                    testId="contact-email"
                  />
                </div>

                <div className="mt-4">
                  <div className="label-eyebrow mb-2">Subject</div>
                  <div className="flex flex-wrap gap-2" data-testid="contact-subjects">
                    {SUBJECTS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => set("subject", s)}
                        className={`px-3.5 py-2 rounded-full text-sm border transition-colors ${
                          f.subject === s
                            ? "bg-forest text-canvas border-forest"
                            : "bg-canvas text-ink/70 border-forest/15 hover:bg-forest/5"
                        }`}
                        data-testid={`contact-subject-${s.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block">
                    <span className="label-eyebrow">Message</span>
                    <textarea
                      value={f.message}
                      onChange={(e) => set("message", e.target.value)}
                      rows={5}
                      placeholder="Tell us what you're building, or what corridor you'd love to see supported..."
                      className="mt-2 w-full rounded-2xl border border-forest/15 bg-canvas focus:bg-white focus:border-forest px-4 py-3 outline-none transition-colors resize-none"
                      data-testid="contact-message"
                    />
                  </label>
                </div>

                {error && (
                  <div className="mt-4 text-sm text-destructive" data-testid="contact-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-accent mt-6"
                  data-testid="contact-submit-btn"
                >
                  {status === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>Send message <ArrowUpRight className="w-4 h-4" /></>
                  )}
                </button>
              </>
            )}
          </motion.form>
        </div>
      </section>
    </>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="card-flat p-6">
      <div className="w-10 h-10 rounded-full bg-forest text-canvas grid place-items-center">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <div className="label-eyebrow mt-4">{title}</div>
      <div className="mt-2 text-ink/75 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="label-eyebrow">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-xl border border-forest/15 bg-canvas focus:bg-white focus:border-forest px-4 py-3 text-ink outline-none transition-colors"
      />
    </label>
  );
}
